import { useDispatch, useSelector } from 'react-redux';
import { useState, useEffect, useCallback } from 'react';

// Use throughout your app instead of plain `useDispatch` and `useSelector`
export const useAppDispatch = useDispatch;
export const useAppSelector = useSelector;

// Returns `value` only after it has stopped changing for `delay` ms.
// Use to feed text-filter inputs into queries without firing a request per keystroke.
export const useDebouncedValue = (value, delay = 300) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
};

// True while `query` matches; tracks viewport changes so breakpoint-gated UI unmounts cleanly.
export const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(
    () => typeof window !== 'undefined' && Boolean(window.matchMedia?.(query).matches)
  );
  useEffect(() => {
    const media = window.matchMedia?.(query);
    if (!media) return undefined;
    const onChange = (event) => setMatches(event.matches);
    setMatches(media.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [query]);
  return matches;
};

// Deciding or deleting the last rows of the final page leaves the pager past
// the end. `setPage` takes the 0-based page it should fall back to.
export const useClampedPage = ({ loading, rowCount, total, pageSize, page, setPage }) => {
  useEffect(() => {
    if (loading || rowCount > 0) return;
    const lastPage = Math.max(0, Math.ceil(total / pageSize) - 1);
    if (page > lastPage) setPage(lastPage);
  }, [loading, rowCount, total, pageSize, page, setPage]);
};

// Controlled row selection for DataTable. `resetKeys` name the filters/pages whose
// change must clear it: hidden rows must never stay selected for a bulk action.
export const useSelection = (resetKeys = []) => {
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const resetKey = resetKeys.join('\u0000');
  useEffect(() => {
    setSelectedIds(new Set());
  }, [resetKey]);
  const clear = useCallback(() => setSelectedIds(new Set()), []);
  return { selectedIds, setSelectedIds, clear };
};
